// The /discuss feed: a composer on top, posts below, each with its comment
// thread. The topic row and tag chips above it are server-rendered
// (posts/_tag_rows) — this app just receives the resulting `tagSlugs`, which
// narrow the feed AND get stamped onto new posts. Also mounted
// (problem-scoped) on problem pages, where `problem` does the narrowing.
// Signed-out visitors read everything and get pointed at /signin to join in.
import { createRoot } from "react-dom/client"
import { type JSX, useEffect, useState } from "react"
import {
  type CommentRecord,
  createComment,
  createPost,
  type DiscussUser,
  fetchFeed,
  type PostRecord,
  type ProblemStub,
  type ReportReason,
} from "./discuss/api"
import { PostMenu } from "./discuss/post_menu"
import type { PictureRecord } from "./pictures/api"
import { AssetManagerModal } from "./pictures/asset_manager_modal"

const POSTS_PER_PAGE = 25

type DiscussProps = {
  feedUrl: string
  postsUrl: string
  picturesUrl: string
  currentUser: DiscussUser | null
  problem: ProblemStub | null
  tagSlugs: string[]
  reportReasons: ReportReason[]
}

function DiscussApp({
  feedUrl,
  postsUrl,
  picturesUrl,
  currentUser,
  problem,
  tagSlugs,
  reportReasons,
}: DiscussProps): JSX.Element {
  const [posts, setPosts] = useState<PostRecord[]>([])
  const [loaded, setLoaded] = useState(false)
  const [failed, setFailed] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  useEffect(() => {
    let cancelled = false
    void fetchFeed(feedUrl, { tags: tagSlugs, problem: problem?.id }).then((fetched) => {
      if (cancelled) return
      if (fetched) {
        setPosts(fetched)
        setHasMore(fetched.length === POSTS_PER_PAGE)
      } else {
        setFailed(true)
      }
      setLoaded(true)
    })
    return () => {
      cancelled = true
    }
  }, [feedUrl, tagSlugs.join(","), problem?.id])

  async function loadMore(): Promise<void> {
    if (loadingMore) return
    setLoadingMore(true)
    const nextPage = page + 1
    const fetched = await fetchFeed(feedUrl, { page: nextPage, tags: tagSlugs, problem: problem?.id })
    setLoadingMore(false)
    if (!fetched) return
    setPage(nextPage)
    setHasMore(fetched.length === POSTS_PER_PAGE)
    setPosts((current) => {
      const known = new Set(current.map((p) => p.id))
      return [ ...current, ...fetched.filter((p) => !known.has(p.id)) ]
    })
  }

  return (
    <div className="discuss">
      {currentUser ? (
        <Composer
          postsUrl={postsUrl}
          picturesUrl={picturesUrl}
          problem={problem}
          tagSlugs={tagSlugs}
          onCreated={(post) => setPosts((current) => [ post, ...current ])}
        />
      ) : (
        <p className="discuss__signin-note">
          <a href="/signin">Sign in</a> to join the discussion.
        </p>
      )}

      {failed && <p className="discuss__note" role="alert">Could not load the discussion.</p>}
      {loaded && !failed && posts.length === 0 && (
        <p className="discuss__note">
          {problem ? "No comments for this problem yet." : "No posts yet."}
        </p>
      )}

      {posts.map((post) => (
        <PostCard
          key={post.id}
          post={post}
          postsUrl={postsUrl}
          currentUser={currentUser}
          showProblem={!problem}
          reportReasons={reportReasons}
        />
      ))}

      {hasMore && (
        <button type="button" className="btn discuss__more" disabled={loadingMore} onClick={() => void loadMore()}>
          {loadingMore ? "Loading…" : "Load more"}
        </button>
      )}
    </div>
  )
}

// ---------- Making a post ----------

function Composer({
  postsUrl,
  picturesUrl,
  problem,
  tagSlugs,
  onCreated,
}: {
  postsUrl: string
  picturesUrl: string
  problem: ProblemStub | null
  tagSlugs: string[]
  onCreated: (post: PostRecord) => void
}): JSX.Element {
  const [draft, setDraft] = useState("")
  const [pictures, setPictures] = useState<PictureRecord[]>([])
  const [assetManagerOpen, setAssetManagerOpen] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The asset manager hands back one picture per Select; duplicates are
  // ignored so reopening and re-picking can't double-attach.
  function attach(picture: PictureRecord): void {
    setPictures((current) => (current.some((p) => p.id === picture.id) ? current : [ ...current, picture ]))
  }

  async function submit(): Promise<void> {
    const text = draft.trim()
    if (sending || (!text && pictures.length === 0)) return
    setSending(true)
    setError(null)
    const result = await createPost(postsUrl, {
      text_contents: text,
      post_type: pictures.length > 0 ? "image" : "text",
      picture_ids: pictures.map((p) => p.id),
      problem_id: problem?.id,
      // New posts land where the reader is: the current topic/tag selection.
      tag_slugs: tagSlugs,
    })
    setSending(false)

    if (typeof result === "string") {
      setError(result)
      return
    }
    setDraft("")
    setPictures([])
    onCreated(result)
  }

  return (
    <form
      className="discuss__composer"
      onSubmit={(event) => {
        event.preventDefault()
        void submit()
      }}
    >
      <textarea
        className="discuss__input discuss__input--area"
        rows={3}
        placeholder={problem ? "Share progress, questions, or ideas on this problem…" : "Share something interesting..."}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />

      {pictures.length > 0 && (
        <ul className="discuss__attachments">
          {pictures.map((picture) => (
            <li key={picture.id} className="discuss__attachment">
              {picture.url && <img src={picture.url} alt={picture.original_file_name ?? "Attached picture"} />}
              <button
                type="button"
                className="discuss__attachment-remove"
                aria-label="Remove picture"
                onClick={() => setPictures((current) => current.filter((p) => p.id !== picture.id))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && <p className="discuss__problem" role="alert">{error}</p>}

      <div className="discuss__composer-row">
        {/* keep_or_mull-style actions bar: a small picture icon bottom-left
            opening the asset manager, the Post button on the right. */}
        <button
          type="button"
          className="discuss__attach-icon"
          aria-label="Add picture"
          title="Add picture"
          onClick={() => setAssetManagerOpen(true)}
        >
          <svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="m21 15-5-5L5 21" />
          </svg>
        </button>
        <button
          type="submit"
          className="btn btn--primary"
          disabled={sending || (!draft.trim() && pictures.length === 0)}
        >
          {sending ? "Posting…" : "Post"}
        </button>
      </div>

      <AssetManagerModal
        picturesUrl={picturesUrl}
        open={assetManagerOpen}
        onClose={() => setAssetManagerOpen(false)}
        onAttach={attach}
      />
    </form>
  )
}

// ---------- Byline ----------

// Avatar, name, and timestamp — the same row on a post and on a comment, so
// the two never drift apart. `avatar_url` is always set by the server (it
// falls back to the anon placeholder), but it is typed nullable, so the
// image is skipped rather than rendering a broken one.
function Byline({
  user,
  at,
  small,
}: {
  user: DiscussUser | null
  at: string
  small?: boolean
}): JSX.Element {
  return (
    <>
      {user?.avatar_url && (
        <img
          className={`discuss__avatar${small ? " discuss__avatar--sm" : ""}`}
          src={user.avatar_url}
          alt=""
        />
      )}
      <span className="discuss__author">
        {user?.name ?? "Someone"}
        {user?.username && <span className="discuss__username">@{user.username}</span>}
      </span>
      <time className="discuss__time" dateTime={at}>
        {relativeTime(at)}
      </time>
    </>
  )
}

// ---------- One post ----------

function PostCard({
  post,
  postsUrl,
  currentUser,
  showProblem,
  reportReasons,
}: {
  post: PostRecord
  postsUrl: string
  currentUser: DiscussUser | null
  showProblem: boolean
  reportReasons: ReportReason[]
}): JSX.Element {
  const [comments, setComments] = useState<CommentRecord[]>(post.comments ?? [])

  return (
    <article className="discuss__post">
      <header className="discuss__post-head">
        <Byline user={post.user} at={post.posted_at ?? post.created_at} />
        <div className="discuss__post-head-end">
          {showProblem && post.problem && (
            <a className="discuss__post-problem" href={post.problem.path ?? "#"}>
              Posted on {post.problem.title}
            </a>
          )}
          {/* Nothing to offer on your own post yet — reporting it would be
              odd, and there's no delete endpoint behind a Delete item. */}
          {!post.is_own && (
            <PostMenu
              postsUrl={postsUrl}
              postId={post.id}
              signedIn={currentUser != null}
              reasons={reportReasons}
            />
          )}
        </div>
      </header>

      {post.text_html && (
        // Server-rendered markdown from post_json — hashtags come back as
        // /research/discuss/topic links, raw HTML is already stripped
        // server-side.
        <div className="discuss__text" dangerouslySetInnerHTML={{ __html: post.text_html }} />
      )}

      {post.pictures.length > 0 && (
        <div className="discuss__pictures">
          {post.pictures.map((picture) =>
            picture.url ? (
              <img
                key={picture.id}
                className="discuss__picture"
                src={picture.url}
                alt={picture.original_file_name ?? "Post picture"}
                width={picture.width ?? undefined}
                height={picture.height ?? undefined}
              />
            ) : null,
          )}
        </div>
      )}

      {post.tags.length > 0 && (
        <ul className="discuss__tags">
          {post.tags.map((tag) => (
            <li key={tag.slug}>
              <a href={`/research/discuss/topic/${encodeURIComponent(tag.slug)}`}>#{tag.display_name}</a>
            </li>
          ))}
        </ul>
      )}

      <CommentThread
        postId={post.id}
        postsUrl={postsUrl}
        comments={comments}
        signedIn={currentUser != null}
        onCreated={(comment) => setComments((current) => [ ...current, comment ])}
      />
    </article>
  )
}

// ---------- Comments ----------

function CommentThread({
  postId,
  postsUrl,
  comments,
  signedIn,
  onCreated,
}: {
  postId: string
  postsUrl: string
  comments: CommentRecord[]
  signedIn: boolean
  onCreated: (comment: CommentRecord) => void
}): JSX.Element | null {
  const [replyingTo, setReplyingTo] = useState<string | null>(null)
  // The post's own composer waits behind a Reply link, the same way each
  // comment's does — a feed of open comment boxes is mostly noise.
  const [composing, setComposing] = useState(false)

  // The thread is stored flat; render top-level comments with their replies
  // (any depth) indented one level beneath them, chronological throughout.
  const topLevel = comments.filter((c) => c.reply_to_comment_id === null)
  const repliesByParent = new Map<string, CommentRecord[]>()
  for (const comment of comments) {
    if (comment.reply_to_comment_id === null) continue
    const rootId = threadRootId(comment, comments)
    repliesByParent.set(rootId, [ ...(repliesByParent.get(rootId) ?? []), comment ])
  }

  // Nothing to show and nothing to do: skip the block entirely rather than
  // leaving its divider rule floating under the post.
  if (!signedIn && comments.length === 0) return null

  return (
    <div className="discuss__comments">
      {topLevel.map((comment) => (
        <div key={comment.id} className="discuss__thread">
          <CommentRow
            comment={comment}
            signedIn={signedIn}
            onReply={() => setReplyingTo(comment.id)}
          />
          {(repliesByParent.get(comment.id) ?? []).map((reply) => (
            <div key={reply.id} className="discuss__reply">
              <CommentRow
                comment={reply}
                signedIn={signedIn}
                onReply={() => setReplyingTo(reply.id)}
              />
            </div>
          ))}
          {replyingTo !== null && threadContains(comments, comment.id, replyingTo) && (
            <div className="discuss__reply">
              <CommentComposer
                postId={postId}
                postsUrl={postsUrl}
                replyToCommentId={replyingTo}
                autoFocus
                onCreated={(created) => {
                  setReplyingTo(null)
                  onCreated(created)
                }}
                onCancel={() => setReplyingTo(null)}
              />
            </div>
          )}
        </div>
      ))}

      {signedIn && (composing ? (
        <CommentComposer
          postId={postId}
          postsUrl={postsUrl}
          autoFocus
          onCreated={(comment) => {
            setComposing(false)
            onCreated(comment)
          }}
          onCancel={() => setComposing(false)}
        />
      ) : (
        <span className="discuss__comment-actions">
          <button type="button" className="discuss__comment-action" onClick={() => setComposing(true)}>
            Reply
          </button>
        </span>
      ))}
    </div>
  )
}

// Walks reply pointers up to the top-level comment so every reply lands in
// its thread regardless of nesting depth.
function threadRootId(comment: CommentRecord, comments: CommentRecord[]): string {
  const byId = new Map(comments.map((c) => [ c.id, c ]))
  let current = comment
  const seen = new Set<string>()
  while (current.reply_to_comment_id !== null && !seen.has(current.id)) {
    seen.add(current.id)
    const parent = byId.get(current.reply_to_comment_id)
    if (!parent) break
    current = parent
  }
  return current.id
}

function threadContains(comments: CommentRecord[], rootId: string, commentId: string): boolean {
  if (rootId === commentId) return true
  const comment = comments.find((c) => c.id === commentId)
  if (!comment) return false
  return threadRootId(comment, comments) === rootId
}

function CommentRow({
  comment,
  signedIn,
  onReply,
}: {
  comment: CommentRecord
  signedIn: boolean
  onReply: () => void
}): JSX.Element {
  return (
    <div className="discuss__comment">
      <div className="discuss__comment-head">
        <Byline user={comment.commenter} at={comment.created_at} small />
      </div>
      <p className="discuss__comment-text">{comment.text_contents}</p>
      {signedIn && (
        <span className="discuss__comment-actions">
          <button type="button" className="discuss__comment-action" onClick={onReply}>
            Reply
          </button>
        </span>
      )}
    </div>
  )
}

function CommentComposer({
  postId,
  postsUrl,
  replyToCommentId,
  autoFocus,
  onCreated,
  onCancel,
}: {
  postId: string
  postsUrl: string
  replyToCommentId?: string
  autoFocus?: boolean
  onCreated: (comment: CommentRecord) => void
  onCancel?: () => void
}): JSX.Element {
  const [draft, setDraft] = useState("")
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(): Promise<void> {
    const text = draft.trim()
    if (!text || sending) return
    setSending(true)
    setError(null)
    const result = await createComment(postsUrl, postId, {
      text_contents: text,
      reply_to_comment_id: replyToCommentId,
    })
    setSending(false)

    if (typeof result === "string") {
      setError(result)
      return
    }
    setDraft("")
    onCreated(result)
  }

  return (
    <form
      className="discuss__comment-composer"
      onSubmit={(event) => {
        event.preventDefault()
        void submit()
      }}
    >
      {error && <p className="discuss__problem" role="alert">{error}</p>}
      <input
        type="text"
        className="discuss__input"
        placeholder={replyToCommentId ? "Write a reply…" : "Write a comment…"}
        autoFocus={autoFocus}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />
      <button type="submit" className="btn btn--primary" disabled={sending || !draft.trim()}>
        {replyToCommentId ? "Reply" : "Comment"}
      </button>
      {onCancel && (
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
      )}
    </form>
  )
}

// "just now", "5m", "3h", "2d", then the date.
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ""
  const seconds = Math.floor((Date.now() - then) / 1000)
  if (seconds < 60) return "just now"
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`
  if (seconds < 7 * 86400) return `${Math.floor(seconds / 86400)}d`
  return new Date(iso).toLocaleDateString()
}

function parseJson<T>(raw: string | undefined): T | null {
  if (!raw) return null
  try {
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

export function mountDiscussApp(): void {
  const el = document.querySelector<HTMLElement>("[data-discuss-app]")
  if (!el) return

  createRoot(el).render(
    <DiscussApp
      feedUrl={el.dataset.discussFeedUrl ?? ""}
      postsUrl={el.dataset.discussPostsUrl ?? ""}
      picturesUrl={el.dataset.discussPicturesUrl ?? ""}
      currentUser={parseJson<DiscussUser>(el.dataset.discussCurrentUser)}
      problem={parseJson<ProblemStub>(el.dataset.discussProblem)}
      tagSlugs={parseJson<string[]>(el.dataset.discussTagSlugs) ?? []}
      reportReasons={parseJson<ReportReason[]>(el.dataset.discussReportReasons) ?? []}
    />
  )
}
